/**
 * ConversationsMenus 组件
 * 从 AZ_AI_MVR_H5/src/pages/OpsMate/components/ConversationsMenus/index.tsx 迁移
 */
import { CloseOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";
import {
  App,
  Button,
  ConfigProvider,
  Flex,
  type GetProp,
  Input,
  Menu,
  Modal,
  Spin,
  Tooltip,
  Typography,
  theme,
} from "antd";
import { createStyles } from "antd-style";
import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from "react";
import type { MenuProps } from "antd/lib";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import MenuCollapsedIcon from "@/assets/opsmate/menu-collapsed.svg";
import HistoryIcon from "@/assets/opsmate/history.svg";
import HoverHistoryIcon from "@/assets/opsmate/ls.svg";
import PinToTop1Icon from "@/assets/opsmate/pin to top1.svg";
import ModificationIcon from "@/assets/opsmate/modification.svg";
import PinToTopIcon from "@/assets/opsmate/pin to top.svg";
import NotTopIcon from "@/assets/opsmate/nottop.svg";
import DeleteIcon from "@/assets/opsmate/delete.svg";
import { Conversations, type ConversationsProps } from "@ant-design/x";
import { useOpsMateContext } from "@/contexts/useOpsMateContext";
import {
  pinConversation,
  unpinConversation,
  editConversationTitle,
  deleteConversations,
  searchConversationContent,
  getGroupedConversations,
} from "@/services/opsmate/conversation";
import { useAuth } from "@/context/auth";
import { getAdminDefaultPathForRoles } from "@/utils/user";

const useStyles = createStyles(({ css }) => ({
  title: {
    color: "#111",
    fontWeight: 600,
  },
  collapseIcon: {
    cursor: "pointer",
  },
  hideScrollbar: css`
    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
    -ms-overflow-style: none;
    overflow: -moz-scrollbars-none;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  `,
}));
const SEARCH_CONVERSATION_PAGE_SIZE = 30;

export default function ConversationsMenus() {
  const { user } = useAuth();
  const {
    askOpsConversations,
    askOpsMateMenus,
    collapsed,
    setCollapsed,
    openSearch,
    setOpenSearch,
    selectedKeys,
    setSelectedKeys,
    resetConversation,
    historyOpsConversations,
    fixFooterOpsConversations,
    pageHeight,
    refreshConversations,
    loadMoreConversations,
    historyConversationsHasMore,
    historyConversationsLoadingMore,
  } = useOpsMateContext();

  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const messageRef = useRef(message);
  messageRef.current = message;
  const { styles } = useStyles();
  const { token } = theme.useToken();

  const [searchAskKeyword, setSearchAskKeyword] = useState("");
  const [hoveredConversationKey, setHoveredConversationKey] = useState<string | null>(null);
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [titleOverrides, setTitleOverrides] = useState<Record<string, string>>({});
  const [searchAskHoveredKey, setSearchAskHoveredKey] = useState<string | null>(null);
  const [searchAskLoading, setSearchAskLoading] = useState(false);
  const [serverSearchConversations, setServerSearchConversations] = useState<
    GetProp<ConversationsProps, "items">
  >([]);
  const [searchAskPage, setSearchAskPage] = useState(1);
  const [searchAskHasMore, setSearchAskHasMore] = useState(true);
  const [searchAskLoadingMore, setSearchAskLoadingMore] = useState(false);
  // 空搜索时由后端按时间分组返回的会话列表
  const [groupedHistoryConversations, setGroupedHistoryConversations] = useState<
    { group: string; items: GetProp<ConversationsProps, "items"> }[]
  >([]);
  const [groupedHistoryLoading, setGroupedHistoryLoading] = useState(false);
  const searchAskKeywordRef = useRef(searchAskKeyword);
  const searchAskRequestIdRef = useRef(0);
  searchAskKeywordRef.current = searchAskKeyword;

  useEffect(() => {
    if (!openSearch) setSearchAskHoveredKey(null);
  }, [openSearch]);

  const finishRename = useCallback(async () => {
    if (!renamingKey) return;
    const value = renameValue.trim();
    if (value) {
      try {
        const res = await editConversationTitle(renamingKey, value);
        const msg = (res && (res as any).Msg) || (typeof res === "string" ? res : "");
        if (msg) messageRef.current.success(msg);
        setTitleOverrides((prev) => ({ ...prev, [renamingKey]: value }));
        await refreshConversations?.();
      } catch (e) {
        console.error("Rename conversation failed", e);
        messageRef.current.error(t("opsmate.menu.renameFailed"));
      }
    }
    setRenamingKey(null);
  }, [renamingKey, renameValue, refreshConversations, t]);

  // ── 根据 createTime 分配时间分组 (T6) ──
  const getTimeGroup = useCallback(
    (createTime: string | undefined): string => {
      if (!createTime) return t("opsmate.menu.older");
      const d = new Date(createTime);
      if (Number.isNaN(d.getTime())) return t("opsmate.menu.older");
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterdayStart = new Date(todayStart.getTime() - 86400000);
      const sevenDaysAgo = new Date(todayStart.getTime() - 7 * 86400000);
      const thirtyDaysAgo = new Date(todayStart.getTime() - 30 * 86400000);
      const sixMonthsAgo = new Date(todayStart.getTime() - 180 * 86400000);
      if (d >= todayStart) return t("opsmate.menu.today");
      if (d >= yesterdayStart) return t("opsmate.menu.yesterday");
      if (d >= sevenDaysAgo) return t("opsmate.menu.last7Days");
      if (d >= thirtyDaysAgo) return t("opsmate.menu.last1Month");
      if (d >= sixMonthsAgo) return t("opsmate.menu.last6Months");
      return t("opsmate.menu.older");
    },
    [t],
  );

  const mapSearchConversations = useCallback(
    (list: any[]): GetProp<ConversationsProps, "items"> =>
      list.map((item: any) => ({
        key: String(item.ID ?? item.id ?? ""),
        label: item.ConversationTitle ?? item.Conversation_ConversationTitle ?? "Untitled",
        group: "Ask OpsMate",
        pinned:
          Number(
            item?.Conversation_Sort ??
              item?.conversation_sort ??
              item?.ConversationSort ??
              item?.conversationSort ??
              0,
          ) !== 0,
        createTime: item.Conversation_CreateTime ?? item.CreateTime ?? "",
      })),
    [],
  );

  // 后端分组 key → 展示文案
  const historyGroupLabels = useMemo<Record<string, string>>(
    () => ({
      Today: t("opsmate.menu.today"),
      Yesterday: t("opsmate.menu.yesterday"),
      Last7Days: t("opsmate.menu.last7Days"),
      LastMonth: t("opsmate.menu.last1Month"),
      Last6Months: t("opsmate.menu.last6Months"),
    }),
    [t],
  );

  // 空搜索：从后端拉取按时间分组的会话列表
  useEffect(() => {
    if (!openSearch || searchAskKeyword.trim()) return;

    let cancelled = false;
    setGroupedHistoryLoading(true);
    (async () => {
      try {
        const res: any = await getGroupedConversations({
          TimezoneOffsetMinutes: new Date().getTimezoneOffset(),
          GroupLimit: 20,
        });
        const rawGroups: any[] =
          (Array.isArray(res?.Groups) && res.Groups) ||
          (Array.isArray(res?.groups) && res.groups) ||
          [];
        const mapped = rawGroups
          .map((g: any) => {
            const key = g?.Group ?? g?.group ?? "";
            const list: any[] =
              (Array.isArray(g?.Items) && g.Items) || (Array.isArray(g?.items) && g.items) || [];
            return {
              group: historyGroupLabels[key] ?? key,
              items: mapSearchConversations(list),
            };
          })
          .filter((g) => g.items.length > 0);
        if (!cancelled) setGroupedHistoryConversations(mapped);
      } catch (e) {
        console.error("Load grouped conversations failed", e);
        if (!cancelled) setGroupedHistoryConversations([]);
      } finally {
        if (!cancelled) setGroupedHistoryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [openSearch, searchAskKeyword, mapSearchConversations, historyGroupLabels]);

  useEffect(() => {
    const keyword = searchAskKeyword.trim();
    if (!openSearch || !keyword) {
      setSearchAskLoading(false);
      setSearchAskLoadingMore(false);
      setSearchAskPage(1);
      setSearchAskHasMore(true);
      setServerSearchConversations([]);
      return;
    }

    let cancelled = false;
    setSearchAskLoading(true);
    const timer = window.setTimeout(async () => {
      const requestId = ++searchAskRequestIdRef.current;
      try {
        const res: any = await searchConversationContent({
          Keyword: keyword,
          Page: 1,
          Limit: SEARCH_CONVERSATION_PAGE_SIZE,
        });
        const list: any[] =
          (Array.isArray(res?.Data) && res.Data) ||
          (Array.isArray(res?.data) && res.data) ||
          (Array.isArray(res) && res) ||
          [];
        const total = Number(res?.Count ?? res?.count) || 0;
        const mapped = mapSearchConversations(list);

        if (cancelled || requestId !== searchAskRequestIdRef.current) return;
        setServerSearchConversations(mapped);
        setSearchAskPage(1);
        setSearchAskHasMore(
          total > 0 ? mapped.length < total : mapped.length === SEARCH_CONVERSATION_PAGE_SIZE,
        );
      } catch (e) {
        console.error("Search conversation content failed", e);
        if (!cancelled) {
          setServerSearchConversations([]);
          setSearchAskHasMore(false);
        }
      } finally {
        if (!cancelled) setSearchAskLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [openSearch, searchAskKeyword, mapSearchConversations]);

  const loadMoreSearchConversations = useCallback(async () => {
    const keyword = searchAskKeywordRef.current.trim();
    if (!openSearch || !keyword || !searchAskHasMore || searchAskLoading || searchAskLoadingMore) {
      return;
    }

    const nextPage = searchAskPage + 1;
    const requestId = ++searchAskRequestIdRef.current;
    setSearchAskLoadingMore(true);
    try {
      const res: any = await searchConversationContent({
        Keyword: keyword,
        Page: nextPage,
        Limit: SEARCH_CONVERSATION_PAGE_SIZE,
      });
      const list: any[] =
        (Array.isArray(res?.Data) && res.Data) ||
        (Array.isArray(res?.data) && res.data) ||
        (Array.isArray(res) && res) ||
        [];
      const total = Number(res?.Count ?? res?.count) || 0;
      const mapped = mapSearchConversations(list);

      if (
        requestId !== searchAskRequestIdRef.current ||
        keyword !== searchAskKeywordRef.current.trim()
      ) {
        return;
      }

      setServerSearchConversations((prev) => {
        const existedKeys = new Set(prev.map((item) => String(item?.key ?? "")));
        const next = [
          ...prev,
          ...mapped.filter((item) => !existedKeys.has(String(item?.key ?? ""))),
        ];
        setSearchAskHasMore(
          mapped.length > 0 &&
            (total > 0 ? next.length < total : mapped.length === SEARCH_CONVERSATION_PAGE_SIZE),
        );
        return next;
      });
      setSearchAskPage(nextPage);
    } catch (e) {
      console.error("Load more conversation content failed", e);
      setSearchAskHasMore(false);
    } finally {
      setSearchAskLoadingMore(false);
    }
  }, [
    mapSearchConversations,
    openSearch,
    searchAskHasMore,
    searchAskLoading,
    searchAskLoadingMore,
    searchAskPage,
  ]);

  const historyItemsForMenu = useMemo(
    () =>
      historyOpsConversations.map((item: any) => {
        const key = item?.key as string;
        // const timeGroup = item?.pinned ? t('opsmate.menu.pinnedGroup') : getTimeGroup(item?.createTime);
        const timeGroup = "Recents";
        const overrideLabelText =
          key && titleOverrides[key] !== undefined
            ? titleOverrides[key]
            : ((item as any)?.label ?? "");
        const tooltipText = (() => {
          const text = String(overrideLabelText ?? "");
          return text.length > 200 ? `${text.slice(0, 200)}...` : text;
        })();

        if (key && key === renamingKey) {
          return {
            ...item,
            group: timeGroup,
            rawLabel: String(overrideLabelText ?? ""),
            label: (
              <Input
                size="small"
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onPressEnter={finishRename}
                onBlur={finishRename}
              />
            ),
          };
        }

        return {
          ...item,
          group: timeGroup,
          rawLabel: String(overrideLabelText ?? ""),
          label: (
            <span
              className="opsmate-history-label"
              style={{ display: "flex", width: "100%", minWidth: 0, alignItems: "center" }}
              onMouseEnter={() => key && setHoveredConversationKey(key)}
              onMouseLeave={() => setHoveredConversationKey((prev) => (prev === key ? null : prev))}
            >
              <Tooltip
                title={tooltipText}
                placement="top"
                arrow={false}
                styles={{
                  container: {
                    height: "auto",
                    background: "#FFFFFF",
                    boxShadow: "0px 0px 24px 8px rgba(77,0,47,0.15)",
                    borderRadius: 8,
                    width: "auto",
                    maxWidth: 160,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "left",
                    padding: "6px 12px",
                    color: "rgba(0,0,0,0.85)",
                    fontSize: 12,
                    lineHeight: "20px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  },
                }}
              >
                <span
                  className="opsmate-history-label-text"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {overrideLabelText}
                </span>
              </Tooltip>
            </span>
          ),
          style:
            key && key === selectedKeys
              ? { background: "rgba(131, 0, 81, 0.10)", borderRadius: 8 }
              : undefined,
        };
      }),
    [historyOpsConversations, renamingKey, renameValue, titleOverrides, selectedKeys, finishRename],
  );

  // 搜索弹窗中按时间分组（与侧边栏 getTimeGroup 一致）
  const TIME_GROUP_ORDER = useMemo(
    () =>
      [
        t("opsmate.menu.today"),
        t("opsmate.menu.yesterday"),
        t("opsmate.menu.last7Days"),
        t("opsmate.menu.last1Month"),
        t("opsmate.menu.last6Months"),
        t("opsmate.menu.older"),
      ] as const,
    [t],
  );

  const groupedSearchConversations = useMemo(() => {
    // 空搜索：直接使用后端按时间分组返回的结果
    if (!searchAskKeyword.trim()) return groupedHistoryConversations;

    // 关键词搜索：服务端结果在前端按时间分组
    const filtered = serverSearchConversations;
    const groups: Record<string, typeof filtered> = {};
    filtered.forEach((item: any) => {
      const g = getTimeGroup(item?.createTime);
      if (!groups[g]) groups[g] = [];
      groups[g].push(item);
    });
    return TIME_GROUP_ORDER.filter((g) => groups[g] && groups[g].length > 0).map((g) => ({
      group: g,
      items: groups[g],
    }));
  }, [
    groupedHistoryConversations,
    searchAskKeyword,
    serverSearchConversations,
    getTimeGroup,
    TIME_GROUP_ORDER,
  ]);

  const groupableConfig: ConversationsProps["groupable"] = {
    label: (group) => (
      <span style={{ fontSize: 14, fontWeight: 400, color: "#666666" }}>{group}</span>
    ),
    defaultExpandedKeys: ["Recents"],
    collapsible: (group) => group == "Recents",
  };

  const handleHistoryScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 50) return;
      if (!historyConversationsHasMore || historyConversationsLoadingMore) return;
      loadMoreConversations();
    },
    [historyConversationsHasMore, historyConversationsLoadingMore, loadMoreConversations],
  );

  const handleSearchResultScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 50) return;
      loadMoreSearchConversations();
    },
    [loadMoreSearchConversations],
  );

  const menuItems: MenuProps["items"] = [
    {
      label: t("opsmate.menu.rename"),
      key: "Rename",
      icon: <img src={ModificationIcon} alt="" aria-hidden />,
    },
    {
      label: t("opsmate.menu.pinToTop"),
      key: "pinToTop",
      icon: <img src={PinToTopIcon} alt="" aria-hidden />,
    },
    {
      label: t("opsmate.menu.unpinFromTop"),
      key: "unpinToTop",
      icon: <img src={NotTopIcon} alt="" aria-hidden />,
    },
    {
      label: <span style={{ color: "#ff4d4f" }}>{t("opsmate.menu.delete")}</span>,
      key: "deleteChat",
      icon: <img src={DeleteIcon} alt="" aria-hidden />,
    },
  ];

  const onClick: MenuProps["onClick"] = (e) => {
    const { key } = e;
    if (key === "search") {
      setOpenSearch(true);
    } else if (key === "add") {
      resetConversation?.();
      navigate("/opsmate");
    } else if (key === "admin") {
      navigate(getAdminDefaultPathForRoles(user?.roles));
    } else if (key === "general-source") {
      navigate("/general-source");
    } else if (key === "personal-source") {
      navigate("/personal-source");
    } else if (key === "study-source") {
      navigate("/study-source");
    } else {
      navigate("/opsmate");
    }
    setSelectedKeys(key);
  };

  const handleDeleteConversation = (conversation: any) => {
    const convId = conversation?.key as string;
    if (!convId) return;
    modal.confirm({
      title: "",
      content: (
        <div>
          <Typography.Title level={5} color="#999">
            {t("opsmate.menu.deleteTitle")}
          </Typography.Title>
          <div>{t("opsmate.menu.deleteDesc")}</div>
        </div>
      ),
      icon: null,
      okText: t("opsmate.menu.confirm"),
      cancelText: t("opsmate.menu.cancel"),
      closable: true,
      centered: true,
      onOk: async () => {
        try {
          await deleteConversations([convId]);
          message.success(t("opsmate.menu.deleted"));
          if (selectedKeys === convId) resetConversation?.();
          await refreshConversations?.();
        } catch (e) {
          console.error("Delete conversation failed", e);
          message.error(t("opsmate.menu.deleteFailed"));
          throw e;
        }
      },
    });
  };

  const menuConfig: ConversationsProps["menu"] = (conversation: any) => {
    const isPinned = !!conversation?.pinned;
    const hideTrigger =
      conversation.group === "MVR with OpsMate" ||
      conversation.key === "search" ||
      conversation.key === "add";
    const convKey = conversation?.key;
    const showMore = convKey && convKey === hoveredConversationKey;

    return {
      trigger: hideTrigger ? (
        <span></span>
      ) : (
        <Flex
          align="center"
          gap={4}
          onMouseEnter={() => convKey && setHoveredConversationKey(convKey)}
          onMouseLeave={() => setHoveredConversationKey((prev) => (prev === convKey ? null : prev))}
          onClick={(e) => e.stopPropagation()}
        >
          {isPinned ? (
            <img
              src={PinToTop1Icon}
              alt=""
              aria-hidden
              className="opsmate-history-pin-icon"
              style={{ flexShrink: 0 }}
            />
          ) : null}
          {showMore ? (
            <span
              className="opsmate-conversation-more"
              style={{ opacity: showMore ? 1 : 0, pointerEvents: "auto" }}
            >
              <ConfigProvider
                theme={{
                  token: { controlHeightSM: 20, lineWidth: 0 },
                  components: { Button: { textHoverBg: "rgba(131, 0, 81, 0.1)" } },
                }}
              >
                <Button
                  size="small"
                  shape="circle"
                  type="text"
                  icon={<MoreOutlined style={{ color: token.colorPrimary }} />}
                />
              </ConfigProvider>
            </span>
          ) : null}
        </Flex>
      ),
      items:
        conversation.group === "MVR with OpsMate"
          ? []
          : menuItems.filter((item) => {
              if (!item || !item.key) return false;
              if (item.key === "pinToTop") return !isPinned;
              if (item.key === "unpinToTop") return isPinned;
              return true;
            }),
      onMouseEnter: () => convKey && setHoveredConversationKey(convKey),
      onMouseLeave: () => setHoveredConversationKey((prev) => (prev === convKey ? null : prev)),
      onClick: async (itemInfo: any) => {
        itemInfo.domEvent.stopPropagation();

        if (itemInfo.key === "deleteChat") {
          handleDeleteConversation(conversation);
          return;
        }

        if (itemInfo.key === "Rename") {
          const key = conversation?.key as string;
          if (!key) return;
          const currentLabel =
            titleOverrides[key] !== undefined
              ? titleOverrides[key]
              : ((conversation as any)?.rawLabel ?? (conversation as any)?.label ?? "");
          setRenamingKey(key);
          setRenameValue(String(currentLabel ?? ""));
          return;
        }

        if (itemInfo.key === "pinToTop") {
          const convId = conversation?.key;
          if (!convId) return;
          try {
            const res = await pinConversation(String(convId));
            const msg = (res && (res as any).Msg) || (typeof res === "string" ? res : "");
            if (msg) message.success(t("opsmate.pinned"));
            await refreshConversations?.();
          } catch (e) {
            console.error("Pin to Top failed", e);
            message.error(t("opsmate.pinFailed"));
          }
          return;
        }

        if (itemInfo.key === "unpinToTop") {
          const convId = conversation?.key;
          if (!convId) return;
          try {
            const res = await unpinConversation(String(convId));
            const msg = (res && (res as any).Msg) || (typeof res === "string" ? res : "");
            if (msg) message.success(t("opsmate.unpinned"));
            await refreshConversations?.();
          } catch (e) {
            console.error("Unpin to Top failed", e);
            message.error(t("opsmate.unpinFailed"));
          }
        }
      },
    };
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            collapsedWidth: 40,
            itemBg: "transparent",
            activeBarBorderWidth: 0,
            itemMarginInline: 0,
            itemSelectedBg: "rgba(131, 0, 81, 0.1)",
            itemBorderRadius: 4,
          },
        },
      }}
    >
      <div
        style={{
          width: !collapsed ? 190 : 40,
          transition: "width 0.2s ease",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: pageHeight,
        }}
      >
        <Flex
          justify={!collapsed ? "space-between" : "center"}
          style={{
            marginTop: 16,
            marginBottom: 14,
            transition: "justify-content 0.2s ease",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: collapsed ? 0 : "fit-content",
              overflow: "hidden",
              whiteSpace: "nowrap",
              visibility: collapsed ? "hidden" : "visible",
              opacity: collapsed ? 0 : 1,
              transition: "width 0.2s ease, opacity 0.2s ease",
              paddingLeft: collapsed ? 0 : 8,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            OpsMate
          </div>
          <Tooltip title={collapsed ? t("opsmate.menu.expand") : t("opsmate.menu.collapse")}>
            <img
              src={MenuCollapsedIcon}
              className={styles.collapseIcon}
              onClick={() => setCollapsed(!collapsed)}
              alt=""
            />
          </Tooltip>
        </Flex>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {collapsed ? (
            <Menu
              className="opsmate-left-menu-collapsed"
              items={askOpsMateMenus}
              inlineCollapsed={collapsed}
              onClick={onClick}
              selectable={false}
              selectedKeys={[selectedKeys]}
            />
          ) : (
            <>
              <div style={{ height: "auto" }}>
                <Conversations
                  activeKey={selectedKeys}
                  rootClassName="opsmate-conversations"
                  onActiveChange={(key) => {
                    onClick?.({
                      key,
                      keyPath: [],
                      item: undefined,
                      domEvent: undefined,
                    });
                  }}
                  items={askOpsConversations}
                  groupable={groupableConfig}
                  menu={menuConfig}
                  styles={{
                    root: { padding: "0px", background: "transparent" },
                    item: { borderRadius: 8, paddingInline: 8 },
                  }}
                />
              </div>
              <div className={styles.hideScrollbar} onScroll={handleHistoryScroll}>
                <Conversations
                  activeKey={selectedKeys}
                  rootClassName="opsmate-conversations"
                  onActiveChange={(key) => {
                    onClick?.({
                      key,
                      keyPath: [],
                      item: undefined,
                      domEvent: undefined,
                    });
                  }}
                  items={historyItemsForMenu}
                  groupable={groupableConfig}
                  menu={menuConfig}
                  styles={{
                    root: { padding: "0px", background: "transparent", height: "auto" },
                    item: { borderRadius: 8, paddingLeft: 8 },
                  }}
                />
                <Conversations
                  activeKey={selectedKeys}
                  rootClassName="opsmate-conversations"
                  onActiveChange={(key) => {
                    onClick?.({
                      key,
                      keyPath: [],
                      item: undefined,
                      domEvent: undefined,
                    });
                  }}
                  items={fixFooterOpsConversations}
                  groupable={groupableConfig}
                  menu={menuConfig}
                  styles={{ root: { padding: "0px", background: "transparent" } }}
                />
                {historyConversationsLoadingMore && (
                  <Flex vertical align="center" justify="center" style={{ padding: 12 }} gap={4}>
                    <Spin size="small" />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {t("opsmate.docs.loadingMore")}
                    </Typography.Text>
                  </Flex>
                )}
                {historyOpsConversations.length >= SEARCH_CONVERSATION_PAGE_SIZE &&
                  !historyConversationsHasMore &&
                  !historyConversationsLoadingMore && (
                    <Typography.Text
                      type="secondary"
                      style={{ display: "block", textAlign: "center", padding: 12, fontSize: 12 }}
                    >
                      {t("opsmate.menu.reachedBottom")}
                    </Typography.Text>
                  )}
              </div>
              {/* <div style={{ height: 214 }}>

              </div> */}
            </>
          )}
        </div>
      </div>

      <Modal
        open={openSearch}
        footer={null}
        title={null}
        width="50%"
        mask={false}
        styles={{
          mask: { backgroundColor: "transparent" },
        }}
        onCancel={() => {
          setOpenSearch(false);
          setSearchAskKeyword("");
        }}
        centered
      >
        <Typography.Title level={5} style={{ marginBottom: 8, fontWeight: 600, fontSize: 18 }}>
          Search Ask
        </Typography.Title>
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, marginBottom: 12 }}
        >
          <Input
            placeholder={t("opsmate.menu.searchPlaceholder")}
            value={searchAskKeyword}
            onChange={(e) => setSearchAskKeyword(e.target.value)}
            onPressEnter={(e) => setSearchAskKeyword((e.target as HTMLInputElement).value)}
            prefix={<SearchOutlined style={{ color: "#8c8c8c", fontSize: 16 }} />}
            allowClear
            size="large"
            style={{ flex: 1 }}
          />
          {/* <Button
            size="large"
            onClick={() => {
              setOpenSearch(false);
              setSearchAskKeyword('');
            }}
            icon={<CloseOutlined />}
          /> */}
        </div>
        {searchAskLoading || groupedHistoryLoading ? (
          <Flex align="center" justify="center" style={{ height: 240 }}>
            <Spin />
          </Flex>
        ) : groupedSearchConversations.length === 0 ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(0,0,0,0.45)",
            }}
          >
            <Typography.Text type="secondary">{t("opsmate.menu.noData")}</Typography.Text>
          </div>
        ) : (
          <div
            className={styles.hideScrollbar}
            onScroll={handleSearchResultScroll}
            style={{ height: 480, overflowY: "auto", marginTop: -4 }}
          >
            {groupedSearchConversations.map(({ group, items: groupItems }) => (
              <div key={group} style={{ marginBlock: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Typography.Text
                    style={{
                      display: "block",
                      color: "#999",
                      lineHeight: "20px",
                      fontSize: 14,
                      paddingLeft: 12,
                    }}
                  >
                    {group}
                  </Typography.Text>
                </div>
                <div>
                  {groupItems.map((item) => (
                    <Flex
                      key={item?.key}
                      style={{
                        background:
                          searchAskHoveredKey === item?.key
                            ? "rgba(131, 0, 80, 0.08)"
                            : "transparent",
                        border: "1px solid transparent",
                        paddingLeft: 12,
                        height: 40,
                        lineHeight: "40px",
                        borderRadius: 8,
                        cursor: "pointer",
                      }}
                      align="center"
                      onMouseEnter={() => item?.key && setSearchAskHoveredKey(item.key as string)}
                      onMouseLeave={() => setSearchAskHoveredKey(null)}
                      onClick={() => {
                        if (item?.key) setSelectedKeys(item.key as string);
                        setOpenSearch(false);
                        setSearchAskKeyword("");
                        navigate("/opsmate");
                      }}
                    >
                      <img
                        src={searchAskHoveredKey === item?.key ? HoverHistoryIcon : HistoryIcon}
                        alt=""
                        aria-hidden
                        style={{ marginRight: 16 }}
                      />
                      <Typography.Text
                        ellipsis
                        style={{
                          color:
                            searchAskHoveredKey === item?.key
                              ? token.colorPrimary
                              : token.colorText,
                        }}
                      >
                        {(item as any)?.label ?? ""}
                      </Typography.Text>
                    </Flex>
                  ))}
                </div>
              </div>
            ))}
            {searchAskLoadingMore && (
              <Flex vertical align="center" justify="center" style={{ padding: 12 }} gap={4}>
                <Spin size="small" />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t("opsmate.docs.loadingMore")}
                </Typography.Text>
              </Flex>
            )}
            {searchAskKeyword.trim() !== "" &&
              serverSearchConversations.length > 0 &&
              !searchAskHasMore &&
              !searchAskLoadingMore && (
                <Typography.Text
                  type="secondary"
                  style={{ display: "block", textAlign: "center", padding: 12, fontSize: 12 }}
                >
                  {t("opsmate.menu.reachedBottom")}
                </Typography.Text>
              )}
          </div>
        )}
        <Typography.Text
          type="secondary"
          style={{
            display: "block",
            textAlign: "center",
            marginTop: 12,
            fontSize: 12,
            color: "rgb(102, 102, 102)",
          }}
        >
          Recent conversations are kept for up to 6 months.
        </Typography.Text>
      </Modal>
    </ConfigProvider>
  );
}
